/**
 * Cart extensions for Blockshop theme.
 * Handles the Web Components logic for the custom Cart Rewards Progress Bar
 * and the Product Recommendations Slider inside the Cart Drawer.
 */

class CartRewardsBar extends HTMLElement {
  connectedCallback() {
    // Initial transition for the fill bar on load
    requestAnimationFrame(() => {
      const fill = this.querySelector('.cart-rewards__bar-fill');
      if (fill) {
        const targetPercent = fill.getAttribute('data-percent') || '0';
        fill.style.width = targetPercent + '%';
      }
    });
  }

  /**
   * Updates the progress bar state in-place from a new parsed DOM node.
   * This prevents tearing down the element and allows CSS transitions to animate smoothly.
   * @param {HTMLElement} newBar The newly rendered cart-rewards-bar element from the AJAX response.
   */
  update(newBar) {
    if (!newBar) return;

    // 1. Update the reward text message
    const currentMsg = this.querySelector('.cart-rewards__message');
    const newMsg = newBar.querySelector('.cart-rewards__message');
    if (currentMsg && newMsg) {
      currentMsg.innerHTML = newMsg.innerHTML;
    }

    // 2. Update the progress bar fill percent
    const currentFill = this.querySelector('.cart-rewards__bar-fill');
    const newFill = newBar.querySelector('.cart-rewards__bar-fill');
    if (currentFill && newFill) {
      const newPercent = newFill.getAttribute('data-percent') || '0';
      currentFill.setAttribute('data-percent', newPercent);
      currentFill.style.width = newPercent + '%';
    }

    // 3. Update milestone nodes
    const currentNodes = this.querySelectorAll('.cart-rewards__milestone-node');
    const newNodes = newBar.querySelectorAll('.cart-rewards__milestone-node');
    currentNodes.forEach((node, index) => {
      const newNode = newNodes[index];
      if (newNode) {
        node.className = newNode.className;
      }
    });

    // 4. Update columns
    const currentCols = this.querySelectorAll('.cart-rewards__column');
    const newCols = newBar.querySelectorAll('.cart-rewards__column');
    currentCols.forEach((col, index) => {
      const newCol = newCols[index];
      if (newCol) {
        col.className = newCol.className;

        // Sync icons inside the milestone icon container
        const currentIcon = col.querySelector('.cart-rewards__icon-container');
        const newIcon = newCol.querySelector('.cart-rewards__icon-container');
        if (currentIcon && newIcon) {
          currentIcon.innerHTML = newIcon.innerHTML;
        }

        // Sync label content
        const currentLabel = col.querySelector('.cart-rewards__label');
        const newLabel = newCol.querySelector('.cart-rewards__label');
        if (currentLabel && newLabel) {
          currentLabel.innerHTML = newLabel.innerHTML;
        }
      }
    });
  }
}

class CartRecommendationsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cart-recommendations__products-track');
    this.leftArrow = this.querySelector('.cart-recommendations__arrow--left');
    this.rightArrow = this.querySelector('.cart-recommendations__arrow--right');
    this.addButtons = this.querySelectorAll('.cart-recommendations__add-button');

    // Arrow navigation events
    if (this.leftArrow && this.rightArrow && this.track) {
      this.leftArrow.addEventListener('click', () => this.scrollSlider(-1));
      this.rightArrow.addEventListener('click', () => this.scrollSlider(1));
      
      // Update arrows on scroll
      this.track.addEventListener('scroll', () => this.updateArrowVisibility(), { passive: true });
      
      // Watch for layout/dimension changes (e.g. drawer opening) to recalculate bounds
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.updateArrowVisibility());
        this.resizeObserver.observe(this.track);
      }

      // Initial check after rendering
      setTimeout(() => this.updateArrowVisibility(), 100);
    }

    // Quick Add button events
    this.addButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleQuickAdd(e));
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Scroll the horizontal container using smooth scrolling
   * @param {number} direction -1 for left, 1 for right
   */
  scrollSlider(direction) {
    if (!this.track) return;
    const itemWidth = this.track.querySelector('.cart-recommendations__product-card')?.offsetWidth || 150;
    const scrollAmount = itemWidth; // Scroll exactly 1 card
    this.track.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth'
    });
  }

  /**
   * Check scroll bounds and enable/disable navigation arrows
   */
  updateArrowVisibility() {
    if (!this.track || !this.leftArrow || !this.rightArrow) return;
    const scrollLeft = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    this.leftArrow.disabled = scrollLeft <= 2;
    this.rightArrow.disabled = scrollLeft >= maxScroll - 2;
  }

  /**
   * AJAX Add to Cart action using the theme's core Cart element framework
   */
  async handleQuickAdd(event) {
    const button = event.currentTarget;
    if (button.getAttribute('aria-busy') === 'true') return;

    const card = button.closest('.cart-recommendations__product-card');
    if (!card) return;

    // Retrieve variant ID from select or hidden input
    let variantId;
    const variantSelect = card.querySelector('.cart-recommendations__variant-select');
    if (variantSelect) {
      variantId = variantSelect.value;
    } else {
      const hiddenInput = card.querySelector('.cart-recommendations__variant-id');
      if (hiddenInput) {
        variantId = hiddenInput.value;
      }
    }

    if (!variantId) return;

    // Show loading spinner state
    button.setAttribute('aria-busy', 'true');

    try {
      // Access the registered Cart custom element class
      const CartClass = customElements.get('cart-element');
      if (!CartClass) {
        throw new Error('cart-element is not defined in customElements registry');
      }

      // Read sections configured for AJAX reload (typically ['cart-drawer'])
      const sections = CartClass.sections || ['cart-drawer'];
      const body = new URLSearchParams({
        id: variantId,
        quantity: 1,
        sections: sections.join(',')
      });

      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      };

      // Call the static addItems method which calls Shopify cart APIs,
      // updates totals, and re-renders the cart UI using section rendering.
      await CartClass.addItems(options, button);
    } catch (err) {
      console.error('[CartExtensions] Error adding product to cart:', err);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

// Define the custom elements
customElements.define('cart-rewards-bar', CartRewardsBar);
customElements.define('cart-recommendations-slider', CartRecommendationsSlider);
